import LegalLayout from "@/pages/legal/LegalLayout";
import { LEGAL } from "@/lib/legal";

const S = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-20">
    <h2 className="font-heading text-2xl font-bold tracking-tight text-white mt-10 mb-3">
      {title}
    </h2>
    <div className="space-y-4 text-zinc-300 text-[15px] leading-relaxed">{children}</div>
  </section>
);

export default function Disclaimer() {
  return (
    <LegalLayout
      testid="disclaimer"
      title="Disclaimer"
      subtitle={`The essentials of what ${LEGAL.brand} does — and doesn't — promise.`}
    >
      <p>
        {LEGAL.brand} is a video-generation platform operated by {LEGAL.company}, based in{" "}
        {LEGAL.country}. Using the platform is subject to this disclaimer.
      </p>

      <S id="content-ownership" title="1. Content ownership">
        <p>
          You keep full ownership of the photos, prompts and settings you upload, and of
          the videos generated from them. {LEGAL.company} takes no ownership stake in any
          of your content. In return, you grant us the minimum licence needed to store the
          files, send them to the chosen AI engine for processing, and deliver the finished
          video back to you.
        </p>
      </S>

      <S id="ai-limits" title="2. AI-generated output — no guarantees">
        <p>
          Videos are produced by third-party generative-AI engines. Outputs may contain
          artefacts, misrepresent architectural details, distort textures or interpret a
          prompt differently from what you intended. We provide the platform{" "}
          <strong>&quot;as is&quot;</strong> and make no warranty that any specific creative
          result will be produced.
        </p>
        <p>
          Before publishing an AI-generated video externally — especially in marketing,
          listings or advertising — <strong>please review every clip yourself</strong> and
          make sure it accurately represents the property or subject.
        </p>
      </S>

      <S id="acceptable-use" title="3. Acceptable use">
        <p>You may not use {LEGAL.brand} to:</p>
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li>Generate content that infringes someone else&apos;s copyright, trademark or personality rights.</li>
          <li>Depict private individuals without their consent.</li>
          <li>Produce sexual, violent, hateful or otherwise unlawful material.</li>
          <li>Impersonate a real person, brand or organisation in a misleading way.</li>
          <li>Circumvent our safety filters or attempt to overload the platform.</li>
        </ul>
        <p>
          We may suspend or terminate any account that violates these rules and we cooperate
          with lawful requests from authorities.
        </p>
      </S>

      <S id="third-party" title="4. Third-party engines &amp; services">
        <p>
          Video renders are executed by third-party AI providers we integrate with. When one
          of those providers is unavailable, unstable or discontinued, videos generated
          against it may fail — you can then <em>Try another AI</em> from the failed-project
          screen to switch to an alternative engine automatically.
        </p>
      </S>

      <S id="liability" title="5. Limitation of liability">
        <p>
          To the maximum extent permitted by Dutch law, {LEGAL.company} is not liable for
          indirect, incidental or consequential damages arising from your use of
          {" "}{LEGAL.brand}. Our total aggregate liability for any claim is limited to the
          fees you paid us in the 3 months preceding the event that gave rise to the claim.
        </p>
        <p>
          Nothing in this disclaimer limits liability that cannot be excluded by mandatory
          consumer law.
        </p>
      </S>

      <S id="refunds" title="6. Credits, refunds &amp; failed renders">
        <p>
          If a render fails due to a platform-side issue, credits are automatically refunded
          to your balance. Purchased credits are non-refundable once used successfully. For
          any billing dispute contact{" "}
          <a className="text-amber-400 hover:underline" href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>
          {" "}within 14 days of the transaction.
        </p>
      </S>

      <S id="law" title="7. Governing law">
        <p>
          This disclaimer is governed by the laws of {LEGAL.country}. Any dispute that
          cannot be resolved amicably shall be brought before the competent Dutch court.
        </p>
      </S>

      <S id="contact" title="8. Contact">
        <p>
          Questions? Email{" "}
          <a className="text-amber-400 hover:underline" href={`mailto:${LEGAL.email}`}>
            {LEGAL.email}
          </a>. All correspondence is handled by email — we do not host a walk-in office.
        </p>
      </S>
    </LegalLayout>
  );
}
